import { memo, useCallback, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";

export interface MessageTemplateDeleteConfirmDialogProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}

const MessageTemplateDeleteConfirmDialog = memo(
  ({ open, onCancel, onConfirm }: MessageTemplateDeleteConfirmDialogProps) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleConfirm = useCallback(async () => {
      if (isSubmitting) return;
      setIsSubmitting(true);
      try {
        await onConfirm();
      } finally {
        setIsSubmitting(false);
      }
    }, [isSubmitting, onConfirm]);

    return (
      <Dialog
        open={open}
        onClose={() => {
          if (isSubmitting) return;
          onCancel();
        }}
        fullWidth
        maxWidth="xs"
        disableRestoreFocus
      >
        <DialogTitle sx={{ pr: 6 }}>
          Remover modelo
          <IconButton
            aria-label="Fechar"
            onClick={onCancel}
            disabled={isSubmitting}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "text.secondary",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Esta ação remove o modelo de mensagem desta clínica no WhatsApp
            Business. E de acordo com a política de uso do Meta você só poderá criar
            um template com o mesmo nome do template removido
            depois de 30 dias após a remoção.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onCancel}
            disabled={isSubmitting}
            sx={{ textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirm}
            disabled={isSubmitting}
            sx={{ textTransform: "none" }}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {isSubmitting ? "Removendo..." : "Remover"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

MessageTemplateDeleteConfirmDialog.displayName =
  "MessageTemplateDeleteConfirmDialog";

export default MessageTemplateDeleteConfirmDialog;
